import React, { type ComponentPropsWithRef, type ComponentType } from "react";
import { useDispatch, useSelector } from "react-redux";
import { type RouteObject, useNavigate } from "react-router-dom-v5-compat";
import type { AppDispatch, TripProduct } from "@b2bportal/core-types";
import { mapQueryResponse } from "@b2bportal/core-utilities";
import {
  HomeItineraryState,
  type HomesReservation,
} from "@b2bportal/homes-api";
import { Product } from "@b2bportal/purchase-api";
import { HomesReservationCard, HomesReservationPage } from "../components";
import { homesPostBookingApi } from "../store";

export type HomesTripProduct = TripProduct<HomesReservation>;

export const useHomesTripProduct = ({
  reservationPath,
}: {
  reservationPath: (reservationId: string) => string;
}): {
  homes: { [Product.Homes]: HomesTripProduct };
  pastHomes: { [Product.Homes]: HomesTripProduct };
  homesReservationRouteProps: RouteObject;
} => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const product = React.useMemo(
    () => ({
      fetch: () => {
        dispatch(
          homesPostBookingApi.endpoints.fetchAllHomesReservations.initiate()
        );
      },
      component: ({
        reservation,
        ...props
      }: ComponentPropsWithRef<
        ComponentType<{ reservation: HomesReservation }>
      >) => (
        <HomesReservationCard
          reservation={reservation}
          selectItinerary={() => {
            navigate(reservationPath(reservation.id.value));
          }}
          {...props}
        />
      ),
    }),
    [dispatch, navigate, reservationPath]
  );

  const reservations = useSelector(
    homesPostBookingApi.endpoints.fetchAllHomesReservations.select()
  );

  return React.useMemo(
    () => ({
      homes: {
        [Product.Homes]: {
          ...product,
          reservations: mapQueryResponse(reservations, (data) => [
            ...(data.itineraries[HomeItineraryState.Future] ?? []),
            ...(data.itineraries[HomeItineraryState.Present] ?? []),
          ]),
        },
      },
      pastHomes: {
        [Product.Homes]: {
          ...product,
          reservations: mapQueryResponse(reservations, (data) => [
            ...(data.itineraries[HomeItineraryState.Past] ?? []),
            ...(data.itineraries[HomeItineraryState.Canceled] ?? []),
          ]),
        },
      },
      homesReservationRouteProps: {
        path: reservationPath(":reservationId"),
        element: <HomesReservationPage />,
      },
    }),
    [product, reservationPath, reservations]
  );
};
